import { useState } from 'react';
import { markVouchersStale, useAppDispatch } from '@ibk/shared-config';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase, ReleaseType } from '@ibk/shared-types';
import { useConfirmVoucherContractRechargeMutation, useUpdateVoucherBeneficiariesMutation, useUpdateVoucherContractDetailMutation, } from '../services/benefitApi';
import { BeneficiaryStatusInProduct } from '../types/voucher-beneficiaries';
export function useCreateRechargeOrder({ voucherContractId, onSuccess, onError, onParametersSaveSuccess, onParametersSaveError, }) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { isFeatureFlagWhiteListed } = useFeatureFlag();
    const isBeneficiaryDiscountEnabled = isFeatureFlagWhiteListed(FeatureFlagUseCase.BENEFICIOS_ENABLE_BENEFICIARY_DISCOUNT, undefined);
    const [confirmRecharge, { isLoading: isConfirming }] = useConfirmVoucherContractRechargeMutation();
    const [updateVoucherDetail, { isLoading: isUpdatingDetail }] = useUpdateVoucherContractDetailMutation();
    const [updateBeneficiaries, { isLoading: isUpdatingBeneficiaries }] = useUpdateVoucherBeneficiariesMutation();
    const [error, setError] = useState(null);
    const isLoading = isConfirming || isUpdatingDetail || isUpdatingBeneficiaries;
    const createOrder = async (orderData, shouldSaveParameters, parametersToSave, product) => {
        try {
            setError(null);
            const result = await confirmRecharge({
                voucherContractId,
                body: orderData,
            }).unwrap();
            if (shouldSaveParameters && parametersToSave && product) {
                try {
                    const voucherData = orderData.vouchers[0];
                    let baseValue;
                    let amountDaily;
                    if (voucherData.releaseType === ReleaseType.MONTHLY) {
                        if (parametersToSave.beneficiaries && parametersToSave.beneficiaries.length > 0) {
                            baseValue = parametersToSave.beneficiaries[0].value ?? 0;
                        }
                    }
                    else {
                        amountDaily = voucherData.amountDaily;
                    }
                    const beneficiariesToUpdate = parametersToSave.beneficiaries.map(ben => {
                        const beneficiary = {
                            value: ben.value ?? 0,
                            status: BeneficiaryStatusInProduct.ACTIVE,
                            contractBeneficiary: ben.beneficiaryId,
                        };
                        if (isBeneficiaryDiscountEnabled && ben.discountValue !== undefined) {
                            beneficiary.discountValue = ben.discountValue;
                        }
                        return beneficiary;
                    });
                    await updateVoucherDetail({
                        voucherContractId,
                        voucherType: parametersToSave.voucherTypeId,
                        body: {
                            deliveryModel: product?.deliveryModel,
                            releaseType: voucherData.releaseType,
                            baseValue,
                            amountDaily,
                            discountType: voucherData.discountType,
                            discountValue: voucherData.discountValue ?? null,
                            rechargeDayType: voucherData.rechargeDayType,
                            rechargeDay: voucherData.rechargeDay ?? null,
                            isIndividualDiscount: voucherData.isIndividualDiscount,
                        },
                    }).unwrap();
                    await updateBeneficiaries({
                        voucherContractId,
                        voucherTypeId: parametersToSave.voucherTypeId,
                        body: {
                            beneficiaries: beneficiariesToUpdate,
                        },
                    }).unwrap();
                    dispatch(markVouchersStale({ contractId: voucherContractId }));
                    onParametersSaveSuccess?.();
                }
                catch (saveError) {
                    onParametersSaveError?.();
                    return;
                }
            }
            onSuccess?.(result.result.id);
        }
        catch (err) {
            const errorMessage = t('recharge.summary.payment.error.message');
            setError(errorMessage);
            onError?.(errorMessage);
        }
    };
    const clearError = () => {
        setError(null);
    };
    return {
        createOrder,
        isLoading,
        error,
        clearError,
    };
}
